<template>
  <div v-if="all_platforms && route.name != 'event-page'">
    <PageHeaderEnv />
    <div :lang="locale" class="min-h-screen flex flex-col bg-background" :class="{ 'lg:pr-16': !isProduction }">
      <div id="header" ref="header" class="sticky top-0 w-full z-10">
        <HeaderAlert v-if="!isB2C" strapi-type="buyer-portal" />
        <HeaderTopBar />
        <div class="shadow bg-header">
          <slot name="header">
            <PageHeader />
          </slot>
        </div>
      </div>

      <div id="content" class="flex flex-col h-full justify-between content">
        <PageContent>
          <BreadCrumbs v-if="safePath && route && !excludedPaths.includes(route.path)" :path="safePath" />
          <slot></slot>
        </PageContent>
        <slot name="extra-content"></slot>
        <slot name="footer">
          <PageFooterBranded />
        </slot>
      </div>
    </div>
  </div>

  <template v-else-if="all_platforms">
    <HeaderTopBar v-if="!isB2C && route.name != 'event-page'" />
    <slot></slot>
  </template>
  <div
    v-else
    class="flex flex-col bg-background bg-white w-full h-full absolute flex justify-center items-center left-0 top-0"></div>
</template>

<script setup lang="ts">
import HeaderTopBar from "@/components/page/HeaderTopBar.vue";
import { isProduction } from "@/config";
import PageHeaderEnv from "./PageHeaderEnv.vue";
import BreadCrumbs from "./BreadCrumbs.vue";
import PageHeader from "./PageHeader.vue";
import PageContent from "./PageContent.vue";
import PageFooterBranded from "./PageFooterBranded.vue";
import { computed, ref, useTemplateRef, watch } from "vue";
import brandconfig from "@/brandconfig";
import { useI18n } from "vue-i18n";
import { setTitle } from "@m-media/vue3-meta-tags";
import { useCssVar, useElementSize } from "@vueuse/core";
import { HeaderAlert } from "@xs2event/js-lib/components";
import { useRoute } from "vue-router";

const isB2C = brandconfig.BRAND !== "xs2event";

const excludedPaths = ["/", "/partners", "/user/profile "];
const { t, locale } = useI18n();

const route = useRoute();

const props = defineProps<{
  path?: Array<{ label: string; to?: string | Record<string, unknown> }>;
  title?: string;
}>();

const safePath = computed(() => props.path);

const pageTitle = computed(() => {
  return props.title ? ` ${t(props.title)} | ${brandconfig.BRAND_SHORT_TITLE}` : brandconfig.BRAND_TITLE;
});

watch(
  pageTitle,
  (title) => {
    setTitle(title);
  },
  { immediate: true }
);

const all_platforms = ref(true);
const deviceType = ref("");
const userAgent = ref("");
const platform = navigator.userAgent.toLowerCase();

const icon = brandconfig.BRAND_ICON;
if (icon) {
  const iconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (iconLink) iconLink.href = icon;
}
if (/(samsung|android|webos|iphone|blackberry|linux armv81|windows phone)/.test(platform)) {
  deviceType.value = "mobile";
} else if (/mac|win|linux/i.test(platform)) {
  deviceType.value = "desktop";
} else if (/tablet|ipad/i.test(platform)) {
  deviceType.value = "tablet";
} else {
  deviceType.value = "unknown";
}
userAgent.value = navigator.userAgent;
if (brandconfig.MOBILE_ONLY === true) {
  if (deviceType.value !== "mobile" && deviceType.value !== "tablet" && deviceType.value !== "unknown") {
    all_platforms.value = false;
  }
}

const header = useTemplateRef("header");

const { height } = useElementSize(header);

const headerHeight = useCssVar("--header-height", document.documentElement);

watch(
  height,
  (height) => {
    headerHeight.value = `${height}px`;
  },
  { immediate: true }
);
</script>
<style scoped>
.v-enter-active,
.v-leave-active {
  transition: opacity 250ms ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
